import { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Industries', href: '#industries' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Benefits', href: '#benefits' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const isDark = theme === 'dark';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? isDark
            ? 'bg-[#0b1f3a]/97 backdrop-blur-md border-b border-white/10 shadow-lg'
            : 'bg-white/97 backdrop-blur-md border-b border-slate-200 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => { e.preventDefault(); handleNav('#home'); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://public.readdy.ai/ai/img_res/3ed4394e-93fa-477b-a2e7-70e95af71618.png"
            alt="Cornerstone AI Solutions Logo"
            className="h-[90px] w-[90px] object-contain"
          />
        </a>

        {/* Desktop Links */}
        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
                className={`text-sm tracking-wide cursor-pointer whitespace-nowrap transition-colors duration-200 ${
                  scrolled
                    ? isDark
                      ? 'text-white/70 hover:text-[#14b8c8]'
                      : 'text-slate-600 hover:text-[#0d7a8a]'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right side: Theme toggle + CTA */}
        <div className="hidden md:flex items-center gap-4">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 ${
              scrolled
                ? isDark
                  ? 'bg-white/10 text-white/70 hover:bg-white/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-lg`}></i>
          </button>

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              scrolled
                ? isDark
                  ? 'bg-[#14b8c8] text-[#0b1f3a] hover:bg-[#12a8b8]'
                  : 'bg-[#0d7a8a] text-white hover:bg-[#0a6878]'
                : 'bg-white/15 text-white border border-white/30 hover:bg-white/25'
            }`}
          >
            Get Started
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>

        {/* Mobile: Theme toggle + Hamburger */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={toggleTheme}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 ${
              scrolled
                ? isDark
                  ? 'bg-white/10 text-white/70'
                  : 'bg-slate-100 text-slate-600'
                : 'bg-white/10 text-white/70'
            }`}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <i className={`${isDark ? 'ri-sun-line' : 'ri-moon-line'} text-lg`}></i>
          </button>
          <button
            className={`w-10 h-10 flex items-center justify-center cursor-pointer ${scrolled ? (isDark ? 'text-white/70' : 'text-slate-700') : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden px-6 py-6 flex flex-col gap-4 shadow-lg ${isDark ? 'bg-[#0b1f3a] border-t border-white/10' : 'bg-white border-t border-slate-100'}`}>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNav(link.href); }}
              className={`text-base py-2 border-b cursor-pointer ${
                isDark
                  ? 'text-white/70 hover:text-[#14b8c8] border-white/10'
                  : 'text-slate-700 hover:text-[#0d7a8a] border-slate-100'
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('#contact'); }}
            className={`mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap ${
              isDark
                ? 'bg-[#14b8c8] text-[#0b1f3a]'
                : 'bg-[#0d7a8a] text-white'
            }`}
          >
            Get Started <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      )}
    </header>
  );
}